<template>
  <CanNotRecordInterview
    v-if="!canRecordInterview"
    :cancel-text="$t('cancel')"
    @cancel="$emit('cancel')"
  >
    <template #description>
      <div class="text-center">
        {{ $t('browserList') }}:
      </div>
    </template>
  </CanNotRecordInterview>
  <VStepper
    v-else
    v-model="step"
    alt-labels
    class="transparent elevation-0"
  >
    <VStepperHeader class="elevation-0 caption text-center stepper-header">
      <VStepperStep :step="1">
        {{ $t('data') }}
      </VStepperStep>
      <VDivider />
      <VStepperStep :step="2">
        {{ $t('cameraOn') }}
      </VStepperStep>
      <VDivider />
      <VStepperStep :step="3" :editable="stepsEditable && step > 3">
        {{ $t('microphone') }}
      </VStepperStep>
      <VDivider />
      <VStepperStep :step="4" :editable="stepsEditable && step > 4">
        {{ $t('camera') }}
      </VStepperStep>
      <VDivider />
      <VStepperStep :step="5">
        {{ $t('interview') }}
      </VStepperStep>
    </VStepperHeader>
    <VStepperItems>
      <VStepperContent :step="1">
        <CandidateCreateWithSearch
          :is-office-interview="true"
          :invitation-type="invitationType"
          :vacancy="vacancy"
          @cancel="$emit('cancel')"
          @created="candidateCreated"
          @selected="candidateSelected($event)"
        />
      </VStepperContent>
      <VStepperContent :step="2">
        <OfficeInterviewRequestMediaPermissions
          v-if="step === 2"
          @done="step = 3"
        />
      </VStepperContent>
      <VStepperContent :step="3">
        <OfficeInterviewMicrophoneCheck
          v-if="step === 3"
          @done="step = 4"
        />
      </VStepperContent>
      <VStepperContent :step="4">
        <OfficeInterviewCameraCheck
          v-if="step === 4"
          @done="step = 5"
        />
      </VStepperContent>
      <VStepperContent :step="5">
        <OfficeInterviewRecorder
          v-if="step === 5"
          :candidate="currentCandidate"
          :resume="resume"
          :overwrite="overwrite"
          @started="recordingStarted"
          @stopped="$emit('stopped')"
          @error="$emit('error')"
        />
      </VStepperContent>
    </VStepperItems>
  </VStepper>
</template>

<script>
import CanNotRecordInterview from '@huntica/huntica-interview-module/src/components/CanNotRecordInterview.vue';
import { canRecordInterview } from '@huntica/huntica-interview-module/src/helpers';
import { hasSelectedMicrophone, hasSelectedCamera, clearGlobalStream } from '@huntica/huntica-interview-module/src/mediaHelpers';
import { candidateInterviewType, invitationType } from '@/enums';
import CandidateCreateWithSearch from '@/forms/CandidateCreateWithSearch';
import useCandidates from '@/compositions/useCandidates';
import CandidateCreateForm from '../../forms/CandidateCreateForm.vue';
import OfficeInterviewRequestMediaPermissions from './OfficeInterviewRequestMediaPermissions.vue';
import OfficeInterviewMicrophoneCheck from './OfficeInterviewMicrophoneCheck.vue';
import OfficeInterviewCameraCheck from './OfficeInterviewCameraCheck.vue';
import OfficeInterviewRecorder from './OfficeInterviewRecorder.vue';

export default {
  name: 'OfficeInterviewRecorderForm',
  components: {
    CandidateCreateWithSearch,
    CanNotRecordInterview,
    OfficeInterviewCameraCheck,
    OfficeInterviewMicrophoneCheck,
    OfficeInterviewRequestMediaPermissions,
    CandidateCreateForm,
    OfficeInterviewRecorder,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    candidate: {
      validator(value) {
        return value instanceof Object || value === null;
      },
      default: null,
    },
    resume: {
      type: Boolean,
      default: false,
    },
    overwrite: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { checkIfAlreadyHasOfficeInterview, showCandidateInterviewType } = useCandidates();

    return {
      checkIfAlreadyHasOfficeInterview,
      showCandidateInterviewType,
    };
  },
  data() {
    return {
      canRecordInterview: canRecordInterview(),
      stepsEditable: true,
      step: this.candidate !== null ? 2 : 1,
      currentCandidate: this.candidate,
      invitationType: invitationType.OFFICE_INTERVIEW,
      globalStream: null,
    };
  },
  async created() {
    if (this.candidate !== null) {
      this.step = await this.resolveStep();
    }
  },
  beforeDestroy() {
    clearGlobalStream();
  },
  methods: {
    async resolveStep() {
      if (await hasSelectedMicrophone() && await hasSelectedCamera()) {
        return 5;
      }

      if (await hasSelectedMicrophone()) {
        return 4;
      }

      return 2;
    },
    async candidateCreated(candidate) {
      this.currentCandidate = candidate;
      this.step = await this.resolveStep();
    },
    async candidateSelected(candidate) {
      const hasInterview = await this.checkIfAlreadyHasOfficeInterview(
        candidate,
        candidateInterviewType.OFFICE_INTERVIEW,
      );

      if (hasInterview) {
        this.$emit('cancel');
        await this.$router.push({
          name: 'candidate',
          params: { candidateId: candidate.id },
          query: { lastInterviewType: candidateInterviewType.OFFICE_INTERVIEW },
        });
      } else {
        this.currentCandidate = candidate;
        this.step = await this.resolveStep();
      }
    },
    recordingStarted() {
      this.stepsEditable = false;
      this.$emit('started');
    },
  },
};
</script>

<style lang="scss" scoped>
.stepper-header {
  &::v-deep {
    .v-stepper__label {
      text-align: center;
    }

    .v-divider {
      border-style: dotted;
      margin-top: 37px;
    }

    .v-stepper__step {
      .v-stepper__step__step {
        background: #F6F6F6 !important;
        color: map-get($grey, 'lighten-1');
        font-size: 14px !important;
      }

      &.v-stepper__step--active {
        .v-stepper__step__step {
          background: map-get($blue, 'base') !important;
          color: #fff;
        }
      }
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  cancel: Cancel
  browserList: 'You need to use the latest version of one of the following:'
  data: Personal data
  permissions: Permissions
  microphone: Mic setup
  camera: Camera setup
  interview: Interview
  candidateAgreed: Candidate agreed to a recorded interview
  cameraOn: Turn on your camera
</i18n>

<i18n locale="ru" lang="yaml">
  cancel: Назад
  browserList: 'Необходимо воспользоваться последней версией одного из следующих браузеров:'
  data: профиль
  permissions: разрешения
  microphone: настройка микрофона
  camera: настройка камеры
  interview: интервью
  candidateAgreed: кандидат дал согласие на запись интервью
  cameraOn: включите камеру
</i18n>
